import { JobActivityStrip } from '../components/terminal/JobActivityStrip';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  Terminal,
  Play,
  Clipboard,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  ChevronDown,
  Hammer,
  Rocket
} from 'lucide-react';

import {
  ScriptCandidate
} from '@kaizen/shared';

import {
  TerminalView
} from '../components/terminal/TerminalView';

import {
  RunScriptModal
} from '../components/modals/RunScriptModal';

import {
  useProjects
} from '../contexts/ProjectContext';

import {
  useRealtime
} from '../contexts/RealtimeContext';


const MAX_SCRIPT_CHARS =
  1_000_000;


/*
 * UX rule:
 *
 * The operator does not choose "Command" vs "Script".
 *
 * KAIZEN decides automatically:
 *
 * one line   -> powershell_command
 * multiline  -> powershell_script
 * very large -> powershell_script
 */
function shouldUseScriptJob(
  value:
    string
): boolean {

  return (
    value.includes(
      '\n'
    ) ||
    value.includes(
      '\r'
    ) ||
    value.length >
      8000
  );
}


export const ConsolePage:
React.FC = () => {

  const {
    projects,
    activeProject
  } =
    useProjects();


  const {
    sendJob,
    device,
    deviceStatus,
    activeJob,
    activeJobLogs
  } =
    useRealtime();


  const [
    selectedProjectId,
    setSelectedProjectId
  ] =
    useState(
      ''
    );


  const [
    input,
    setInput
  ] =
    useState(
      ''
    );


  const [
    selectedScript,
    setSelectedScript
  ] =
    useState<
      ScriptCandidate |
      null
    >(
      null
    );


  const [
    isApprovalOpen,
    setIsApprovalOpen
  ] =
    useState(
      false
    );


  const [
    isSubmitting,
    setIsSubmitting
  ] =
    useState(
      false
    );


  const [
    error,
    setError
  ] =
    useState<
      string |
      null
    >(
      null
    );
  const [
    deployConfirmOpen,
    setDeployConfirmOpen
  ] =
    useState(
      false
    );


  const inputRef =
    useRef<HTMLTextAreaElement>(
      null
    );


  useEffect(
    () => {

      if (
        selectedProjectId
      ) {
        return;
      }


      const initial =
        activeProject ||
        projects[0];


      if (
        initial?.id
      ) {

        setSelectedProjectId(
          initial.id
        );
      }

    },
    [
      selectedProjectId,
      activeProject,
      projects
    ]
  );


  const currentProject =
    projects.find(
      project =>
        project.id ===
        selectedProjectId
    ) ||
    activeProject ||
    projects[0];
  /*
   * Console project-context resolver.
   *
   * Registered project roots are authoritative.
   * If pasted PowerShell clearly references one project root,
   * the Console switches to that project before Build/Deploy/run.
   */
  const resolveConsoleProject =
    (
      value:
        string
    ) => {

      const normalized =
        value.toLowerCase();


      const directMatches =
        projects
          .filter(
            project =>
              Boolean(
                project.localPath
              ) &&
              normalized.includes(
                project.localPath.toLowerCase()
              )
          )
          .sort(
            (
              left,
              right
            ) =>
              right.localPath.length -
              left.localPath.length
          );


      if (
        directMatches.length >
        0
      ) {

        return directMatches[0];
      }


      const locationMatch =
        value.match(
          /(?:Set-Location|Push-Location|(?:^|\r?\n)\s*cd)\s+(?:-LiteralPath\s+|-Path\s+)?["']?([A-Za-z]:\\[^\r\n;]+)/i
        );


      if (
        !locationMatch?.[1]
      ) {

        return null;
      }


      const detectedPath =
        locationMatch[1]
          .trim()
          .replace(
            /["']+$/,
            ''
          )
          .toLowerCase();


      return (
        projects
          .filter(
            project => {

              const root =
                project.localPath
                  ?.replace(
                    /[\\/]+$/,
                    ''
                  )
                  .toLowerCase();


              if (
                !root
              ) {

                return false;
              }


              return (
                detectedPath ===
                  root ||
                detectedPath.startsWith(
                  `${root}\\`
                ) ||
                detectedPath.startsWith(
                  `${root}/`
                )
              );
            }
          )
          .sort(
            (
              left,
              right
            ) =>
              right.localPath.length -
              left.localPath.length
          )[0] ||
        null
      );
    };


  const updateConsoleInput =
    (
      value:
        string
    ) => {

      setInput(
        value
      );


      const detectedProject =
        resolveConsoleProject(
          value
        );


      if (
        detectedProject?.id &&
        detectedProject.id !==
          selectedProjectId
      ) {

        setSelectedProjectId(
          detectedProject.id
        );

        setDeployConfirmOpen(
          false
        );
      }
    };

  const consoleIsIdle =
    activeJobLogs.length ===
      0 &&
    (
      !activeJob ||
      [
        'succeeded',
        'failed',
        'cancelled',
        'timed_out'
      ].includes(
        activeJob.status
      )
    );


  const trimmed =
    input.trim();


  const isScript =
    shouldUseScriptJob(
      input
    );


  const exceedsLimit =
    input.length >
    MAX_SCRIPT_CHARS;


  const sizeKb =
    useMemo(
      () =>
        (
          new Blob(
            [
              input
            ]
          ).size /
          1024
        ).toFixed(
          1
        ),
      [
        input
      ]
    );
  /*
   * Console V3 deliberately uses a fixed-height composer.
   * Large scripts scroll internally and never push the UI upward.
   */

const prepareExecution =
    () => {

      if (
        !trimmed ||
        exceedsLimit ||
        isSubmitting ||
        deviceStatus !==
          'online'
      ) {
        return;
      }
      /*
       * KAIZEN_V34_PREPARE_CONTEXT
       *
       * A project root found inside the PowerShell is authoritative.
       * The dropdown changes AND the ScriptCandidate is created with
       * that project's working directory.
       */
      const executionProject =
        resolveConsoleProject(
          input
        ) ||
        currentProject;


      if (
        executionProject?.id &&
        executionProject.id !==
          selectedProjectId
      ) {

        setSelectedProjectId(
          executionProject.id
        );
      }


      const now =
        new Date().toISOString();


      const candidate:
        ScriptCandidate = {

        id:
          `console-${crypto.randomUUID()}`,

        filename:
          isScript
            ? 'CONSOLE_SCRIPT.ps1'
            : 'CONSOLE_COMMAND.ps1',

        type:
          isScript
            ? 'powershell_script'
            : 'powershell_command',

        size:
          new Blob(
            [
              input
            ]
          ).size,

        source:
          'KAIZEN Console',

        receivedTime:
          now,

        status:
          'pending',

        command:
          isScript
            ? ''
            : trimmed,

        scriptContent:
          isScript
            ? input
            : undefined,

        workingDirectory:
          executionProject?.localPath ||
          'F:\\KAIZEN REMOTE'
      };


      setSelectedScript(
        candidate
      );


      setIsApprovalOpen(
        true
      );
    };


  const execute =
    async (
      script:
        ScriptCandidate
    ) => {

      setIsApprovalOpen(
        false
      );


      setError(
        null
      );


      setIsSubmitting(
        true
      );

      /*
       * Optimistic composer clear.
       * Do not wait for network/job creation before clearing UI.
       */
      setInput(
        ''
      );



      try {

        /*
         * KAIZEN_V34_SEND_CONTEXT
         *
         * React state can still be one render behind after auto-switch.
         * Resolve the project again from ScriptCandidate.workingDirectory.
         */
        const executionProject =
          projects.find(
            project => {

              const scriptRoot =
                script.workingDirectory
                  ?.replace(
                    /[\\/]+$/,
                    ''
                  )
                  .toLowerCase();


              const projectRoot =
                project.localPath
                  ?.replace(
                    /[\\/]+$/,
                    ''
                  )
                  .toLowerCase();


              if (
                !scriptRoot ||
                !projectRoot
              ) {
                return false;
              }


              return (
                scriptRoot === projectRoot ||
                scriptRoot.startsWith(
                  `${projectRoot}\\`
                ) ||
                scriptRoot.startsWith(
                  `${projectRoot}/`
                )
              );
            }
          ) ||
          currentProject;


        const scriptJob =
          script.type ===
          'powershell_script';


        await sendJob(
          {
            deviceId:
              device?.id ||
              '',

            projectId:
              executionProject?.id,

            type:
              scriptJob
                ? 'powershell_script'
                : 'powershell_command',

            payload:
              scriptJob
                ? {
                    scriptContent:
                      script.scriptContent ||
                      script.command ||
                      ''
                  }
                : {
                    command:
                      script.command ||
                      script.scriptContent ||
                      ''
                  },

            workingDirectory:
              script.workingDirectory ||
              executionProject?.localPath ||
              'F:\\KAIZEN REMOTE'
          }
        );


        setInput(
          ''
        );

      }
      catch (
        exception:
          unknown
      ) {

        setInput(
          script.scriptContent ||
          script.command ||
          ''
        );


        setError(
          exception instanceof Error
            ? exception.message
            : String(
                exception
              )
        );

      }
      finally {

        setIsSubmitting(
          false
        );
      }
    };


  const paste =
    async () => {

      try {

        const text =
          await navigator.clipboard.readText();


        if (
          text
        ) {

          updateConsoleInput(text);


          requestAnimationFrame(
            () => {

              const element =
                inputRef.current;


              if (
                !element
              ) {
                return;
              }


              element.focus();

              element.setSelectionRange(
                0,
                0
              );

              element.scrollTop =
                0;
            }
          );
        }

      }
      catch {

        setError(
          'Clipboard permission is unavailable. Long-press inside the PowerShell box and paste normally.'
        );
      }
    };
  const activeJobBusy =
    Boolean(
      activeJob &&
      ![
        'succeeded',
        'failed',
        'cancelled',
        'timed_out'
      ].includes(
        activeJob.status
      )
    );


  const triggerConsoleBuild =
    async () => {

      if (
        !currentProject ||
        !device?.id ||
        deviceStatus !==
          'online' ||
        activeJobBusy ||
        isSubmitting
      ) {

        return;
      }


      setError(
        null
      );

      setDeployConfirmOpen(
        false
      );

      setIsSubmitting(
        true
      );


      try {

        await sendJob(
          {
            deviceId:
              device.id,

            projectId:
              currentProject.id,

            type:
              'build',

            payload: {
              command:
                currentProject.buildCommand
            },

            workingDirectory:
              currentProject.localPath
          }
        );

      }
      catch (
        exception:
          unknown
      ) {

        setError(
          exception instanceof Error
            ? `Build dispatch failed: ${exception.message}`
            : `Build dispatch failed: ${String(exception)}`
        );

      }
      finally {

        setIsSubmitting(
          false
        );
      }
    };


  const triggerConsoleProductionDeploy =
    async () => {

      if (
        !currentProject ||
        !device?.id ||
        deviceStatus !==
          'online' ||
        activeJobBusy ||
        isSubmitting ||
        currentProject.productionEnabled ===
          false
      ) {

        return;
      }


      setError(
        null
      );

      setDeployConfirmOpen(
        false
      );

      setIsSubmitting(
        true
      );


      try {

        await sendJob(
          {
            deviceId:
              device.id,

            projectId:
              currentProject.id,

            type:
              'deploy_production',

            payload: {
              projectName:
                currentProject.cloudflareProjectName ||
                'production-project',

              args: [
                currentProject.buildOutputDirectory
              ]
            },

            workingDirectory:
              currentProject.localPath
          }
        );

      }
      catch (
        exception:
          unknown
      ) {

        setError(
          exception instanceof Error
            ? `Production deploy failed: ${exception.message}`
            : `Production deploy failed: ${String(exception)}`
        );

      }
      finally {

        setIsSubmitting(
          false
        );
      }
    };

  const triggerConsoleLocalLaunch =
    async () => {

      const launchProject =
        resolveConsoleProject(
          input
        ) ||
        currentProject;


      if (
        !launchProject ||
        !device?.id ||
        deviceStatus !==
          'online' ||
        activeJobBusy ||
        isSubmitting
      ) {
        return;
      }


      if (
        launchProject.id !==
          selectedProjectId
      ) {
        setSelectedProjectId(
          launchProject.id
        );
      }


      setError(
        null
      );

      setDeployConfirmOpen(
        false
      );


      const psQuote =
        (
          value:
            string
        ) =>
          value.replace(
            /'/g,
            "''"
          );


      const root =
        psQuote(
          launchProject.localPath
        );


      const projectName =
        psQuote(
          launchProject.name
        );


      const devCommand =
        psQuote(
          launchProject.devCommand ||
          'npm run dev'
        );


      const isVite =
        launchProject.projectType ===
          'vite';


      /*
       * Deliberately built from normal JS strings rather than a
       * nested template literal. This avoids TypeScript / PowerShell
       * quoting collisions.
       */
      const launchScript =
        [
          "$ErrorActionPreference = 'Stop'",
          "$root = '" + root + "'",
          "$projectName = '" + projectName + "'",
          "$devCommand = '" + devCommand + "'",
          "$isVite = " + (
            isVite
              ? '$true'
              : '$false'
          ),
          "$port = 5173",
          "while ($port -le 5199 -and (Get-NetTCPConnection -LocalPort $port -State Listen -ErrorAction SilentlyContinue)) { $port++ }",
          "if ($port -gt 5199) { throw 'No free local development port found between 5173 and 5199.' }",
          "$serverCommand = $devCommand",
          "if ($isVite) { $serverCommand = $devCommand + ' -- --host 0.0.0.0 --port ' + $port + ' --strictPort' }",
          "$safeRoot = $root.Replace(\"'\", \"''\")",
          "$safeName = $projectName.Replace(\"'\", \"''\")",
          "$safeServer = $serverCommand.Replace(\"'\", \"''\")",
          "$childCommand = \"Set-Location -LiteralPath '$safeRoot'; `$Host.UI.RawUI.WindowTitle = 'KAIZEN LOCAL - $safeName'; Write-Host ''; Write-Host '============================================================' -ForegroundColor Cyan; Write-Host ' KAIZEN LOCAL - $safeName' -ForegroundColor Cyan; Write-Host '============================================================' -ForegroundColor Cyan; Write-Host ''; Write-Host 'CTRL+C stops the dev server.' -ForegroundColor Yellow; Write-Host ('Local URL: http://localhost:' + $port) -ForegroundColor Green; Write-Host ''; Invoke-Expression '$safeServer'\"",
          "Start-Process -FilePath 'powershell.exe' -ArgumentList @('-NoExit','-NoLogo','-ExecutionPolicy','Bypass','-Command',$childCommand)",
          "Start-Sleep -Seconds 2",
          "Start-Process ('http://localhost:' + $port)",
          "Write-Output ('[KAIZEN LOCAL] Visible PowerShell launched for ' + $projectName)",
          "Write-Output ('[Working Directory] ' + $root)",
          "Write-Output ('[Local URL] http://localhost:' + $port)",
          "Write-Output '[Control] CTRL+C in the visible PowerShell stops the server.'"
        ].join(
          '\n'
        );


      setIsSubmitting(
        true
      );


      try {

        await sendJob(
          {
            deviceId:
              device.id,

            projectId:
              launchProject.id,

            type:
              'powershell_script',

            payload: {
              scriptContent:
                launchScript
            },

            workingDirectory:
              launchProject.localPath
          }
        );

      }
      catch (
        exception:
          unknown
      ) {

        setError(
          exception instanceof Error
            ? `Local launch failed: ${exception.message}`
            : `Local launch failed: ${String(exception)}`
        );

      }
      finally {

        setIsSubmitting(
          false
        );
      }
    };

  const headerAccessory =
    (
      <div className="relative flex items-center gap-1.5 min-w-0 w-full">

        <div className="relative min-w-0 flex-1 sm:flex-none">

          <select
            aria-label="Project"
            value={
              selectedProjectId
            }
            onChange={
              event => {

                setSelectedProjectId(
                  event.target.value
                );

                setDeployConfirmOpen(
                  false
                );
              }
            }
            title={
              currentProject?.localPath ||
              currentProject?.name ||
              'Select project'
            }
            className="appearance-none w-full min-w-0 max-w-none sm:w-auto sm:max-w-[260px] pl-2 pr-6 py-1.5 bg-transparent hover:bg-white/[0.05] border border-transparent hover:border-white/[0.08] text-slate-400 hover:text-slate-200 text-[10px] sm:text-[11px] font-sans font-medium rounded-md focus:outline-none focus:bg-slate-800 focus:border-slate-700 focus:ring-1 focus:ring-indigo-500 truncate cursor-pointer"
          >

            {projects.map(
              project => (

                <option
                  key={
                    project.id
                  }
                  value={
                    project.id
                  }
                >
                  {project.name}
                </option>

              )
            )}

          </select>


          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />

        </div>


        <div className="h-4 w-px bg-white/[0.08] mx-0.5" />


        <button
          type="button"
          onClick={
            () =>
              void triggerConsoleBuild()
          }
          disabled={
            !currentProject ||
            !device?.id ||
            deviceStatus !==
              'online' ||
            activeJobBusy ||
            isSubmitting
          }
          title={
            currentProject
              ? `Build ${currentProject.name}`
              : 'Build project'
          }
          aria-label="Build current project"
          className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-transparent text-slate-400 hover:text-indigo-300 hover:bg-indigo-500/[0.08] hover:border-indigo-400/[0.12] disabled:opacity-30 disabled:pointer-events-none transition-colors"
        >
          <Hammer className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={
            () =>
              void triggerConsoleLocalLaunch()
          }
          disabled={
            !currentProject ||
            !device?.id ||
            deviceStatus !==
              'online' ||
            activeJobBusy ||
            isSubmitting
          }
          title={
            currentProject
              ? `Launch ${currentProject.name} locally in visible PowerShell`
              : 'Launch project locally'
          }
          aria-label="Launch current project locally"
          className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-transparent text-slate-400 hover:text-emerald-300 hover:bg-emerald-500/[0.08] hover:border-emerald-400/[0.12] disabled:opacity-30 disabled:pointer-events-none transition-colors"
        >
          <Play className="w-3.5 h-3.5" />
        </button>


        <div className="relative">

          <button
            type="button"
            onClick={
              () =>
                setDeployConfirmOpen(
                  value =>
                    !value
                )
            }
            disabled={
              !currentProject ||
              !device?.id ||
              deviceStatus !==
                'online' ||
              activeJobBusy ||
              isSubmitting ||
              currentProject?.productionEnabled ===
                false
            }
            title={
              currentProject
                ? `Deploy ${currentProject.name} to production`
                : 'Deploy project'
            }
            aria-label="Deploy current project"
            className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-transparent text-slate-400 hover:text-violet-300 hover:bg-violet-500/[0.08] hover:border-violet-400/[0.12] disabled:opacity-30 disabled:pointer-events-none transition-colors"
          >
            <Rocket className="w-3.5 h-3.5" />
          </button>


          {deployConfirmOpen && (
            <div className="absolute z-50 right-0 top-[calc(100%+8px)] w-[230px] rounded-xl border border-white/[0.09] bg-slate-950/95 backdrop-blur-xl shadow-2xl shadow-black/40 p-2.5">

              <div className="text-[10px] font-semibold text-slate-200">
                Production deploy
              </div>

              <div className="mt-1 text-[9px] leading-relaxed text-slate-500">
                Deploy {currentProject?.name} using its registered Cloudflare project and output directory?
              </div>


              <div className="mt-2.5 flex items-center justify-end gap-1.5">

                <button
                  type="button"
                  onClick={
                    () =>
                      setDeployConfirmOpen(
                        false
                      )
                  }
                  className="h-7 px-2.5 rounded-md border border-white/[0.08] bg-white/[0.03] text-[9px] font-medium text-slate-400 hover:text-slate-200 hover:bg-white/[0.06]"
                >
                  Cancel
                </button>


                <button
                  type="button"
                  onClick={
                    () =>
                      void triggerConsoleProductionDeploy()
                  }
                  className="h-7 px-2.5 rounded-md border border-violet-400/20 bg-violet-500/15 text-[9px] font-semibold text-violet-200 hover:bg-violet-500/25"
                >
                  Deploy
                </button>

              </div>

            </div>
          )}

        </div>

      </div>
    );



  const composer =
    (
      <div className="p-2.5 sm:p-3">

        {error && (

          <div
            className="
              mb-2
              flex items-start justify-between gap-2
              rounded-lg
              border border-rose-500/20
              bg-rose-500/[0.07]
              px-3 py-2
              text-[10px] sm:text-[11px]
              text-rose-300
            "
          >

            <span className="min-w-0 break-words">
              {error}
            </span>


            <button
              type="button"
              onClick={
                () =>
                  setError(
                    null
                  )
              }
              className="
                shrink-0
                text-rose-400
                hover:text-rose-200
              "
              aria-label="Dismiss error"
            >
              ×
            </button>

          </div>
        )}


        <div
          className="
            overflow-hidden
            rounded-xl
            border border-white/[0.09]
            bg-[#020817]
            shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]
            focus-within:border-indigo-400/50
            focus-within:ring-1
            focus-within:ring-indigo-400/15
            transition-colors
          "
        >

          {/* Editor surface */}
          <div className="flex items-start min-w-0">

            <div
              className="
                shrink-0
                pl-3
                pt-3.5
                font-mono
                text-xs
                text-indigo-400
                select-none
              "
            >
              PS&gt;
            </div>


            <textarea
              ref={
                inputRef
              }
              value={
                input
              }
              onChange={
                event =>
                  setInput(
                    event.target.value
                  )
              }
              onPaste={
                () => {

                  /*
                   * When a complete script is pasted into an empty
                   * composer, present it from line 1 rather than
                   * dropping the operator at the bottom.
                   */
                  if (
                    input.length !==
                      0
                  ) {
                    return;
                  }


                  window.setTimeout(
                    () => {

                      const element =
                        inputRef.current;


                      if (
                        !element
                      ) {
                        return;
                      }


                      element.scrollTop =
                        0;

                      element.setSelectionRange(
                        0,
                        0
                      );

                    },
                    0
                  );
                }
              }
              onKeyDown={
                event => {

                  if (
                    event.ctrlKey &&
                    event.key ===
                      'Enter'
                  ) {

                    event.preventDefault();

                    prepareExecution();
                  }
                }
              }
              disabled={
                isSubmitting
              }
              rows={1}
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="Type or paste PowerShell..."
              className="
                block
                flex-1
                min-w-0
                h-[104px]
                min-h-[104px]
                max-h-[104px]
                resize-none
                overflow-y-auto
                bg-transparent
                px-3
                py-3
                font-mono
                text-xs
                leading-5
                text-slate-100
                placeholder:text-slate-600
                focus:outline-none
                disabled:opacity-60
                [scrollbar-width:thin]
              "
            />

          </div>


          {/* Composer toolbar - never overlaps script text */}
          <div
            className="
              min-h-[46px]
              flex items-center
              gap-2
              border-t border-white/[0.07]
              bg-slate-900/60
              px-2.5 py-1.5
            "
          >

            <div className="flex items-center gap-0.5 shrink-0">

              <button
                type="button"
                onClick={
                  () =>
                    void paste()
                }
                disabled={
                  isSubmitting
                }
                title="Paste from clipboard"
                aria-label="Paste PowerShell"
                className="
                  w-8 h-8
                  inline-flex
                  items-center justify-center
                  rounded-md
                  text-slate-400
                  hover:text-slate-100
                  hover:bg-white/[0.06]
                  disabled:opacity-30
                  transition-colors
                "
              >
                <Clipboard className="w-3.5 h-3.5" />
              </button>


              <button
                type="button"
                onClick={
                  () =>
                    setInput(
                      ''
                    )
                }
                disabled={
                  !input ||
                  isSubmitting
                }
                title="Clear PowerShell"
                aria-label="Clear PowerShell"
                className="
                  w-8 h-8
                  inline-flex
                  items-center justify-center
                  rounded-md
                  text-slate-500
                  hover:text-rose-300
                  hover:bg-rose-500/[0.06]
                  disabled:opacity-20
                  transition-colors
                "
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>

            </div>


            <div
              className="
                flex-1
                min-w-0
                flex items-center
                gap-1.5
                overflow-hidden
                text-[9px] sm:text-[10px]
              "
            >

              {exceedsLimit ? (

                <span className="inline-flex items-center gap-1 text-rose-400 truncate">

                  <AlertTriangle className="w-3 h-3 shrink-0" />

                  Script too large

                </span>

              ) : (

                <span className="inline-flex items-center gap-1 text-emerald-400/70 truncate">

                  <ShieldCheck className="w-3 h-3 shrink-0" />

                  Review required

                </span>

              )}


              <span className="text-slate-700">
                ·
              </span>


              <span className="text-slate-500 truncate">
                {isScript
                  ? 'Script'
                  : 'Command'}
              </span>

            </div>


            <div
              className="
                shrink-0
                font-mono
                text-[9px] sm:text-[10px]
                text-slate-500
              "
            >
              {sizeKb} KB
            </div>


            <button
              type="button"
              onClick={
                prepareExecution
              }
              disabled={
                !trimmed ||
                exceedsLimit ||
                isSubmitting ||
                deviceStatus !==
                  'online'
              }
              title="Review and run"
              aria-label="Review and run PowerShell"
              className="
                shrink-0
                w-9 h-9
                inline-flex
                items-center justify-center
                rounded-lg
                bg-indigo-600
                text-white
                shadow-[0_0_18px_rgba(79,70,229,0.18)]
                hover:bg-indigo-500
                active:scale-[0.97]
                disabled:bg-slate-800
                disabled:text-slate-600
                disabled:shadow-none
                transition-all
              "
            >
              <Play className="w-4 h-4 fill-current" />
            </button>

          </div>

        </div>

      </div>
    );


  return (
    <div
      className={`flex flex-col gap-2.5 overflow-hidden ${
        consoleIsIdle
          ? 'min-h-0'
          : 'h-[calc(100dvh-9.5rem)] md:h-[calc(100vh-8rem)] min-h-[430px]'
      }`}
    >

      {/* Minimal page identity only */}
      <div className="shrink-0 flex items-center justify-between gap-2 px-0.5">

        <div className="flex items-center gap-2 min-w-0">

          <Terminal className="w-5 h-5 text-indigo-600 shrink-0" />

          <h2 className="text-lg sm:text-xl font-bold text-slate-900 truncate">
            PowerShell Console
          </h2>

        </div>


        <div
          className={`shrink-0 w-2 h-2 rounded-full ${
            deviceStatus ===
              'online'
              ? 'bg-emerald-500'
              : 'bg-rose-500'
          }`}
          title={
            deviceStatus ===
              'online'
              ? 'Windows Agent online'
              : 'Windows Agent offline'
          }
        />

      </div>


      {/* One integrated terminal surface */}
      <div
        className={
          consoleIsIdle
            ? 'shrink-0 flex flex-col gap-2'
            : 'flex-1 min-h-0 flex flex-col gap-2 overflow-hidden'
        }
      >

        <>
          <JobActivityStrip />

          {/* KAIZEN_CONSOLE_CONTROL_BAR */}
          <div
            className="
              shrink-0
              mt-2
              mb-2
              min-w-0
              flex items-center
              px-2.5 py-2
              rounded-xl
              border border-white/[0.07]
              bg-slate-900/55
              backdrop-blur-xl
              shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]
            "
          >
            {headerAccessory}
          </div>

                    {/* KAIZEN_TERMINAL_REMAINING_HEIGHT */}
          <div
            className={
              consoleIsIdle
                ? 'shrink-0'
                : 'flex-1 min-h-0 overflow-hidden'
            }
          >
<TerminalView
                    title="Terminal"
                    fillAvailable={
                      !consoleIsIdle
                    }
                    maxHeight="h-[38dvh] min-h-[250px] max-h-[390px] md:h-[460px] md:max-h-[460px]"
                    headerAccessory={undefined}
                    footer={
                      composer
                    }
                  />
          </div>
        </>

      </div>


      {/* Existing explicit operator execution gate */}
      <RunScriptModal
        script={
          selectedScript
        }
        workingDirectory={
          currentProject?.localPath ||
          'F:\\KAIZEN REMOTE'
        }
        projectName={
          currentProject?.name
        }
        isOpen={
          isApprovalOpen
        }
        onClose={
          () =>
            setIsApprovalOpen(
              false
            )
        }
        onConfirm={
          script =>
            void execute(
              script
            )
        }
      />

    </div>
  );
};





